import React, { useContext, createContext, useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import CircleLeftArrow from "../../Components/Icons/CircleLeftArrow";
import CircleRightArrow from "../../Components/Icons/CircleRightArrow";
import { useTranslation } from "react-i18next";
import Settings from "../../Components/Icons/Settings";
import { GetAccess, GetMultipleAccess } from "../../Components/Utils/roles";
import { useSidebar } from "../../Context/SidebarContext";

const LocalSidebarContext = createContext();

const isSubItemActive = (subLink, currentPathname) => {
  if (!subLink || !currentPathname) return false;
  const current = currentPathname.toLowerCase().replace(/\/$/, "");
  const target = (subLink.startsWith("/") ? subLink : `/${subLink}`).toLowerCase().replace(/\/$/, "");
  return current === target || current.startsWith(target + "/");
};

const Submenu = ({ subItems }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [topPosition, setTopPosition] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const screenHeight = window.innerHeight;
    const submenuHeight = subItems.length * 75; // Adjust this value based on the actual submenu item height
    if (screenHeight < submenuHeight) {
      console.log(screenHeight - submenuHeight);
      setTopPosition(screenHeight - submenuHeight);
    }
    setIsVisible(true);
  }, [subItems]);

  return (
    <div
      style={{ top: `${topPosition}px` }}
      className={`
    absolute left-full rounded-md px-1 ml-1
    bg-indigo-100 text-indigo-800 text-left
    invisible opacity-20 -translate-x-3 transition-all
    group-hover:visible group-hover:opacity-100 group-hover:translate-x-0 w-52 text-sm z-50
`}
    >
      {subItems.map((subItem, subIndex) => {
        const isSubActive = isSubItemActive(subItem?.link, location.pathname);
        return (
          <React.Fragment key={subIndex}>
            {GetAccess(subItem?.hasAccess || "") && (
              <Link
                key={subIndex}
                to={`${subItem.link}`}
                className={`flex justify-start my-1 rounded-md px-3 py-[3px] xl:py-2 transition-colors ${isSubActive
                  ? "bg-[#048178] text-white font-semibold shadow-xs"
                  : "bg-[#E6F2F2] text-gray-800 hover:bg-[#048178] hover:text-[#E6F2F2]"
                  }`}
              >
                <p
                  className={` text-[11px] 2xl:text-sm text-left ${i18n.language == "ar" ? "text-right" : "text-left"
                    }`}
                >
                  {t(subItem.name)}
                </p>
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default function Sidebar({ children }) {
  const [expanded, setExpanded] = useState(true);
  const { mobileOpen, closeMobile } = useSidebar();
  const { t, i18n } = useTranslation();

  function getExpand() {
    let locExp = localStorage.getItem("expand");
    if (locExp) {
      setExpanded(JSON.parse(locExp));
    } else {
      localStorage.setItem("expand", true);
      setExpanded(true);
    }
  }

  useEffect(() => {
    getExpand();
  }, []);

  function toggleExpanded(e) {
    setExpanded(e);
    localStorage.setItem("expand", e);
  }

  return (
    <>
      {/* Laptop / Desktop Sidebar (visible on screens >= 1024px) */}
      <aside className="h-full z-[997] hidden lg:block">
        <nav className="h-full flex flex-col bg-white border-r shadow-sm">
          <div className="p-4 pb-2 flex justify-between items-center">
            <div
              className={`overflow-hidden font-medium text-xl text-baseColor-primary text-center transition-all ${expanded ? "w-32" : "w-0"
                }`}
            >
              nutrade
            </div>
            <button
              onClick={() => toggleExpanded(!expanded)}
              className="p-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer"
            >
              {expanded ? <CircleLeftArrow /> : <CircleRightArrow />}
            </button>
          </div>
          <div className=" w-full h-[calc(100vh-120px)] ">
            <LocalSidebarContext.Provider value={{ expanded, isMobileDrawer: false }}>
              <ul
                className="flex-1 px-3"
                dir={i18n.language == "ar" ? "rtl" : ""}
              >
                {children}
              </ul>
            </LocalSidebarContext.Provider>
          </div>

          {GetMultipleAccess([
            "company_info_view",
            "product_view",
            "marketing_view",
            "procurement_view",
            "sales_view",
            "payment_view",
            "store_view",
            "shipping_view",
            "maintenance_view",
            "user_view",
            "role_view",
            "user_role_view",
            "notification_view",
            "reference_view",
          ]) && (
              <div className="border-t flex p-3">
                <Link className="flex justify-between items-center" to="/settings">
                  <div className="p-2 bg-[#c7d2fe] rounded-lg">
                    <Settings color="#048178" height={18} width={18} />
                  </div>
                </Link>
                <Link className="flex justify-between items-center" to="/settings">
                  <div
                    className={`
                flex justify-between items-center
                 overflow-hidden transition-all ${expanded ? "w-28 xl:w-32 ml-3" : "w-0"
                      }
            `}
                  >
                    <div className="leading-4">
                      <h4 className="font-semibold text-xs 2xl:text-sm">
                        Settings
                      </h4>
                    </div>
                  </div>
                </Link>
              </div>
            )}

          <div className=" w-full px-4">
            <p
              className={`font-normal text-left ${expanded ? "text-xs" : "text-[10px]"
                } text-slate-400`}
            >
              <a href="https://numota.in" target="_blank" rel="noreferrer">
                {expanded && <span>&#169;</span>} numota
              </a>
            </p>
          </div>
        </nav>
      </aside>

      {/* Mobile Responsive Drawer Slider (visible on screens < 1024px) */}
      <div className="lg:hidden">
        {/* Backdrop Overlay */}
        {mobileOpen && (
          <div
            className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-[9998] transition-opacity duration-300"
            onClick={closeMobile}
          />
        )}

        {/* Drawer Container Panel */}
        <div
          className={`fixed top-0 left-0 bottom-0 w-[300px] max-w-[85vw] bg-white z-[9999] shadow-2xl flex flex-col transition-transform duration-300 ease-in-out ${mobileOpen ? "translate-x-0" : "-translate-x-full"
            }`}
        >
          {/* Mobile Drawer Header with Close Button */}
          <div className="p-4 flex items-center justify-between border-b border-slate-100">
            <button
              onClick={closeMobile}
              className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors focus:outline-none cursor-pointer"
              aria-label="Close drawer"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
            <div className="font-semibold text-xl text-baseColor-primary">
              nutrade
            </div>
            <div className="w-6" />
          </div>

          {/* Mobile Drawer Content List */}
          <div className="flex-1 overflow-y-auto px-4 py-3">
            <LocalSidebarContext.Provider
              value={{ expanded: true, isMobileDrawer: true, closeMobile }}
            >
              <ul className="space-y-1" dir={i18n.language == "ar" ? "rtl" : ""}>
                {children}
              </ul>
            </LocalSidebarContext.Provider>
          </div>

          {/* Mobile Drawer Footer */}
          {GetMultipleAccess([
            "company_info_view",
            "product_view",
            "marketing_view",
            "procurement_view",
            "sales_view",
            "payment_view",
            "store_view",
            "shipping_view",
            "maintenance_view",
            "user_view",
            "role_view",
            "user_role_view",
            "notification_view",
            "reference_view",
          ]) && (
              <div className="border-t border-slate-100  px-3" onClick={closeMobile}>
                <Link
                  className="flex items-center gap-3 p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50"
                  to="/settings"
                >
                  <div className="p-2 bg-[#c7d2fe] rounded-lg">
                    <Settings color="#048178" height={18} width={18} />
                  </div>
                  <span className="font-semibold text-xs text-slate-800">
                    Settings
                  </span>
                </Link>
              </div>
            )}

          <div className="w-full px-4 pb-4">
            <p className="font-normal text-xs text-slate-400 text-left">
              <a href="https://numota.in" target="_blank" rel="noreferrer">
                <span>&#169;</span> numota
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

export function SidebarItem({
  icon,
  text,
  active,
  alert,
  hasSubMenu,
  subItem,
  link,
  hideOnMobile,
}) {
  const { expanded, isMobileDrawer, closeMobile } = useContext(LocalSidebarContext);
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const hasActiveChild = Boolean(
    subItem && subItem.some((sub) => isSubItemActive(sub?.link, location.pathname))
  );

  const [isOpen, setIsOpen] = useState(active || hasActiveChild);

  useEffect(() => {
    if (active || hasActiveChild) {
      setIsOpen(true);
    }
  }, [active, hasActiveChild, location.pathname]);

  const handleMobileClick = () => {
    if (isMobileDrawer && closeMobile) {
      closeMobile();
    }
  };

  const toggleAccordion = () => {
    setIsOpen((prev) => !prev);
  };

  if (isMobileDrawer && hideOnMobile) {
    return null;
  }

  if (isMobileDrawer) {
    // Mobile Drawer item layout (Collapsible Accordion & Green theme colors)
    return (
      <li className="list-none my-1">
        {!hasSubMenu ? (
          <Link to={link} onClick={handleMobileClick}>
            <div
              className={`
                relative flex items-center px-3 py-1.5
                font-medium rounded-xl border cursor-pointer
                transition-all duration-150 shadow-xs
                ${active
                  ? "bg-[#048178] border-[#048178] text-white font-medium"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-[#E6F2F2] hover:text-[#048178] hover:border-[#048178]/30"
                }
              `}
            >
              <span className="w-5 flex justify-center items-center mr-2.5 text-base">
                {icon}
              </span>

              <span className="flex-1 text-xs 2xl:text-sm font-medium">
                {text}
              </span>

              {alert && (
                <div
                  className={`w-2 h-2 rounded-full ${active ? "bg-white" : "bg-[#048178]"
                    }`}
                />
              )}
            </div>
          </Link>
        ) : (
          <div className="relative">
            {/* Parent Accordion Header (not highlighted as solid active) */}
            <div
              onClick={toggleAccordion}
              className={`
                relative flex items-center justify-between px-3 py-1.5
                font-medium rounded-xl border cursor-pointer
                transition-all duration-150 shadow-xs
                ${hasActiveChild
                  ? "bg-[#E6F2F2]/70 border-[#048178]/40 text-[#048178]"
                  : "bg-white border-slate-200 text-slate-700 hover:bg-[#E6F2F2] hover:text-[#048178] hover:border-[#048178]/30"
                }
              `}
            >
              <div className="flex items-center flex-1 min-w-0">
                <span className="w-5 flex justify-center items-center mr-2.5 text-base">
                  {icon}
                </span>
                <span className="text-xs 2xl:text-sm font-medium truncate">
                  {text}
                </span>
              </div>

              <div className="flex items-center ml-2">
                {alert && (
                  <div className="w-2 h-2 rounded-full bg-[#048178] mr-2" />
                )}
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${isOpen ? "rotate-180 text-[#048178]" : "text-slate-400"
                    }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* Accordion Sub-items List */}
            {isOpen && subItem && subItem.length > 0 && (
              <div className="ml-4 my-1 pl-2 border-l-2 border-[#048178]/30 space-y-1 transition-all">
                {subItem.map((sub, subIdx) => {
                  const isSubActive = isSubItemActive(sub?.link, location.pathname);
                  return (
                    <React.Fragment key={subIdx}>
                      {GetAccess(sub?.hasAccess || "") && (
                        <Link
                          to={sub.link}
                          onClick={handleMobileClick}
                          className={`block py-1.5 px-3 rounded-lg text-xs font-medium transition-all ${isSubActive
                            ? "bg-[#048178] text-white font-semibold shadow-xs"
                            : "bg-slate-50 text-slate-700 hover:bg-[#E6F2F2] hover:text-[#048178]"
                            }`}
                        >
                          {t(sub.name)}
                        </Link>
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </li>
    );
  }

  // Desktop view layout
  return (
    <>
      {!hasSubMenu ? (
        <Link to={link}>
          <li
            className={`
        relative flex items-center py-1 xl:py-2 px-3 my-1
        font-medium rounded-md cursor-pointer
        transition-colors group
        ${active == true
                ? "bg-gradient-to-tr from-indigo-200 to-indigo-100 text-indigo-800"
                : "hover:bg-indigo-50 text-gray-600"
              }
    `}
          >
            <span className=" w-4 justify-center items-center text-xs 2xl:text-sm">
              {icon}
            </span>

            <span
              className={`overflow-hidden transition-all text-xs 2xl:text-sm ${expanded
                ? `w-28 xl:w-32 ${i18n.language == "ar" ? "mr-3" : "ml-3"} `
                : "w-0"
                }`}
            >
              {text}
            </span>
            {alert && (
              <div
                className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${expanded ? "" : "top-2"
                  }`}
              />
            )}
          </li>
        </Link>
      ) : (
        <li
          className={`
      relative flex items-center py-1 xl:py-2 px-3 my-1
      font-medium rounded-md cursor-pointer
      transition-colors group
      ${hasActiveChild
              ? "bg-indigo-50 text-indigo-800"
              : "hover:bg-indigo-50 text-gray-600"
            }
  `}
        >
          <span className=" w-4 flex justify-center items-center text-xs 2xl:text-sm">
            {icon}
          </span>

          <span
            className={`overflow-hidden transition-all text-xs 2xl:text-sm ${expanded
              ? `w-28 xl:w-32 ${i18n.language == "ar" ? "mr-3" : "ml-3"} `
              : "w-0"
              }`}
          >
            {text}
          </span>
          {alert && (
            <div
              className={`absolute right-2 w-2 h-2 rounded bg-indigo-400 ${expanded ? "" : "top-2"
                }`}
            />
          )}

          <Submenu subItems={subItem || []} />
        </li>
      )}
    </>
  );
}
